// src/pages/candidate/CandidateDashboard.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import {
  VideoCameraIcon,
  CheckCircleIcon,
  ClockIcon,
  BellIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

const CandidateDashboard = () => {
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['candidate-dashboard'],
    queryFn: () => apiService.getCandidateDashboard(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Pending Interviews',
      value: dashboard?.pending_interviews || 0,
      icon: ClockIcon,
      color: 'bg-yellow-100 text-yellow-800',
      link: '/candidate/interviews',
    },
    {
      name: 'Completed Interviews',
      value: dashboard?.completed_interviews || 0,
      icon: CheckCircleIcon,
      color: 'bg-green-100 text-green-800',
      link: '/candidate/interviews',
    },
    {
      name: 'Unread Notifications',
      value: dashboard?.unread_notifications || 0,
      icon: BellIcon,
      color: 'bg-blue-100 text-blue-800',
      link: '/candidate/notifications',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="card bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Welcome back, {dashboard?.candidate?.name}!</h1>
            <p className="text-primary-100 mt-1">
              Ready to take on your next interview challenge?
            </p>
          </div>
          <div className="hidden md:block">
            <img
              className="h-16 w-16 rounded-full border-4 border-primary-300"
              src={dashboard?.candidate?.profile_picture || `https://ui-avatars.com/api/?name=${dashboard?.candidate?.name}&background=fff&color=3b82f6`}
              alt={dashboard?.candidate?.name}
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="card hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Notifications */}
      {dashboard?.recent_notifications?.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Notifications</h2>
            <Link to="/candidate/notifications" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {dashboard.recent_notifications.slice(0, 3).map((notification) => (
              <div key={notification.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <BellIcon className="h-5 w-5 text-primary-600 mt-0.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                  <p className="text-sm text-gray-600">{notification.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </p>
                </div>
                {!notification.is_read && (
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upcoming Interviews */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Upcoming Interviews</h3>
            <Link to="/candidate/interviews" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              View all
            </Link>
          </div>
          
          {dashboard?.pending_interviews > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center">
                  <VideoCameraIcon className="h-8 w-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Interview Scheduled</p>
                    <p className="text-xs text-gray-600">You have pending interviews to complete</p>
                  </div>
                </div>
                <Link
                  to="/candidate/interviews"
                  className="btn-primary text-sm"
                >
                  View Details
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <VideoCameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No upcoming interviews</p>
              <p className="text-sm text-gray-400">Check back later for new interview invitations</p>
            </div>
          )}
        </div>

        {/* Resume Upload */}
        {/* <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Resume</h3>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors duration-200">
            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900 mb-1">Upload your resume</p>
            <p className="text-xs text-gray-500 mb-4">PDF, DOC, or DOCX up to 10MB</p>
            
            <label className="btn-primary cursor-pointer inline-block">
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    // Handle file upload
                    console.log('File selected:', e.target.files[0]);
                  }
                }}
              />
              Choose File
            </label>
          </div>
        </div> */}
      </div>

      {/* Interview Tips */}
      <div className="card bg-blue-50 border border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-900">Interview Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>Test your microphone and camera before starting</li>
                <li>Find a quiet, well-lit space for your interview</li>
                <li>Speak clearly and take your time to answer questions</li>
                <li>You can type notes while speaking to help with transcription</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDashboard;